import { ApiError } from '@/api/types'
import { Roles } from '@/entities/roles'
import { TopupModel, TopupModelMerchant } from '@/entities/topup'
import { USER_ROLE_KEY } from '@/features/auth'
import { DisputModel } from '@/features/orders/model/types'
import { formatCardOrPhone } from '@/features/orders/utils/formatCardOrPhone'
import { getDisputAmount } from '@/features/orders/utils/getDisputAmount'
import { renderActionButton } from '@/features/orders/utils/renderActionButton'
import { formatRequisitToType } from '@/features/requisites'
import { Skeleton } from '@/shared/components/shadcn'
import { useToast } from '@/shared/hooks'
import { formatCurrency } from '@/shared/lib/currency'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useEffect } from 'react'
import { DownloadPicture } from '@/features/orders'
import { Receipt } from '@/entities/receipt'
import { Currencies } from '@/entities/curency'

type Props = {
  disput: DisputModel
  topup: TopupModel | TopupModelMerchant | undefined
  openDisputDialog: (id: string) => void
  topupError: ApiError | null
  topupIsLoad: boolean
  deviceName?: string
}

export const MobileOrderBanner = ({
  disput,
  openDisputDialog,
  topup,
  topupError,
  topupIsLoad,
  deviceName,
}: Props) => {
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const isTrader = userRole === Roles.Trader
  const isMerchant = userRole === Roles.Merchant
  const { addToast } = useToast()

  useEffect(() => {
    if (topupError) {
      addToast('Ошибка загрузки данных', topupError.message, 'error')
    }
  }, [topupError, addToast])

  const blockClasses =
    'rounded-2xl bg-light-purple dark:bg-dark-purple flex flex-col p-6 border-b-1 border-b-card-border dark:border-b-card-border-dark last:border-b-0'

  const AmountBlock = ({ title, value }: { title: string; value: string }) => (
    <div className="flex justify-between font-medium text-text-secondary">
      <span>{title}</span>
      <span>{value}</span>
    </div>
  )

  const InfoBlock = ({
    title,
    value,
    subtext,
  }: {
    title: string
    value?: string
    subtext?: string
  }) => (
    <div className="flex flex-col">
      <span className="mb-2 text-text-secondary">{title}</span>
      {topupIsLoad ? (
        <div className="flex flex-col gap-1">
          <Skeleton className="w-full h-5" />
          <Skeleton className="w-full h-5" />
        </div>
      ) : (
        <>
          <span>{value || ''}</span>
          {subtext && <span className="font-klein font-medium text-text-secondary">{subtext}</span>}
        </>
      )}
    </div>
  )

  const { disputAmountUSDT, disputAmountRub } = getDisputAmount(disput)

  if (isTrader) {
    const traderData = { ...topup } as TopupModel

    return (
      <div className="flex flex-col gap-4 text-sm">
        {/* Блок суммы */}
        <div className={`${blockClasses}`}>
          <div className="flex flex-col gap-2 pb-4">
            <span className="font-klein font-medium text-[18px]">
              {formatCurrency(disputAmountUSDT, Currencies.USDT)}
            </span>
            <div>
              <AmountBlock
                title="Сумма сделки:"
                value={
                  traderData
                    ? formatCurrency(traderData.amountFrom, traderData.currencyFrom)
                    : '0 RUB'
                }
              />
              <AmountBlock
                title="Сумма спора:"
                value={formatCurrency(disputAmountRub, Currencies.RUB)}
              />
            </div>
          </div>
          <div className="flex flex-col py-4">
            <InfoBlock
              title="Реквизит"
              value={formatCardOrPhone(traderData.paymentRequisitProfile?.value ?? '')}
              subtext={traderData.requisitProfile?.bankToName ?? ''}
            />
          </div>
          <div className="flex flex-col mt-4">
            <InfoBlock
              title="Устройство"
              value={deviceName}
              subtext={traderData?.deviceProfile?.id ?? ''}
            />
          </div>
        </div>

        {/* Блок с инф-й о споре */}
        <div className={`${blockClasses} flex flex-col gap-[30px]`}>
          <div>
            <div className="flex flex-col gap-2 pb-4">
              <span className="text-text-secondary">Информация о споре</span>
              <span className="font-medium">{disput.description ?? 'Нет данных'}</span>
            </div>
            <div className="flex flex-col gap-2 pt-4">
              <span className="inline-block text-text-secondary">Прикрепленный чек</span>
              <DownloadPicture disput={disput} />
            </div>
          </div>
          {renderActionButton({ disput, openDialog: openDisputDialog })}
        </div>
      </div>
    )
  }

  if (isMerchant) {
    const merchantData = { ...topup } as TopupModelMerchant

    return (
      <div className="flex flex-col gap-4 text-sm">
        {/* Блок суммы */}
        <div className={`${blockClasses}`}>
          <div className="flex flex-col gap-2 pb-4">
            <span className="font-klein font-medium text-[18px]">{disputAmountUSDT}</span>
            <div>
              <AmountBlock
                title="Сумма сделки:"
                value={
                  merchantData
                    ? formatCurrency(merchantData.amountFrom, merchantData.currencyFrom)
                    : '0 RUB'
                }
              />
              <AmountBlock
                title="Сумма спора:"
                value={formatCurrency(disputAmountRub, Currencies.RUB)}
              />
            </div>
          </div>
          <div className="flex flex-col py-4">
            <InfoBlock
              title="Реквизит"
              value={formatCardOrPhone(
                formatRequisitToType(
                  merchantData.paymentRequisitToType,
                  merchantData.paymentRequisitToValue
                ) ?? ''
              )}
            />
          </div>
        </div>

        {/* Блок с инф-й о споре */}
        <div className={`${blockClasses} flex flex-col gap-[30px]`}>
          <div>
            <div className="flex flex-col gap-2 pb-4">
              <span className="text-text-secondary">Информация о споре</span>
              <span className="font-medium">
                {disput.description ? disput.description : 'Нет данных'}
              </span>
            </div>
            <div className="flex flex-col gap-2 pt-4">
              <span className="inline-block text-text-secondary">Прикрепленный чек</span>
              <Receipt receiptFile={disput.receiptFile} receiptType={disput.receiptType} />
            </div>
          </div>
        </div>
      </div>
    )
  }
}
